import _ from "lodash";
import { useMemo } from "react";
import { useDataMap } from "../../common/hooks/api/useDataMap";
import { NetworkRequestState } from "../../common/models";
import {
  useDataMapCollectedPIC,
  useDataMapReceived,
} from "../../common/service/resources/data-map/dataMapTransformers";
import { CollectionGroupDetailsDto } from "../../common/service/server/dto/CollectionGroupDto";
import { CategoryDto, ReceivedDto } from "../../common/service/server/dto/datamap/DataMapDto";
import { DataMapOperation } from "../../common/service/server/dto/datamap/DataMapRequestDto";
import {
  CollectionContext,
  OrganizationDataSourceDto,
} from "../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationPublicId } from "../../common/service/server/Types";
import { dataSourceSort } from "../surveys/util";
import { useOrgDataSources } from "./useOrganizationVendors";
import { isBuiltInSourceVendor } from "../util/vendors";

type UpdateReceivedArgs = { vendorId: string; receivedIds: string[]; received: boolean };
export type UseReceivedForCollectionGroups = {
  requests: { sourcesRequest: NetworkRequestState; dataMapRequest: NetworkRequestState };
  actions: {
    updateReceived: (args: UpdateReceivedArgs) => void;
  };
  sources: OrganizationDataSourceDto[];
  sourcesWithCollectedPIC: OrganizationDataSourceDto[];
  received: ReceivedDto[];
  collectedPIC: CategoryDto[];
  complete: boolean;
  pendingComplete: boolean;
};

export const useReceivedForCollectionGroups = (
  orgId: OrganizationPublicId,
  collectionGroups: CollectionGroupDetailsDto[],
  collectionContext: CollectionContext[] = ["CONSUMER"],
  limitToExistingAssociations: boolean = false,
): UseReceivedForCollectionGroups => {
  const { data: dataMap, request: dataMapRequest, mutators } = useDataMap(orgId);
  const { patch: patchDataMap } = mutators;
  const [sources, sourcesRequest] = useOrgDataSources(orgId);

  const relevantGroupIds = useMemo(() => collectionGroups.map((g) => g.id), [collectionGroups]);

  const associatedVendors = useMemo(() => {
    const associations = dataMap.sourceAssociations.filter((a) =>
      relevantGroupIds.includes(a.groupId),
    );
    return new Set(associations.map((a) => a.vendorId));
  }, [dataMap, relevantGroupIds]);

  const sourcesWithCollectedPIC = useMemo(() => {
    return (
      sources
        ?.filter(
          (r) =>
            associatedVendors.has(r.vendorId) &&
            _.intersection(r.collectionContext, collectionContext).length > 0,
        )
        ?.sort(dataSourceSort) ?? []
    );
  }, [sources, collectionContext, associatedVendors]);

  const contextualSources = useMemo(() => {
    return (
      sources
        ?.filter(
          (r) =>
            _.intersection(r.collectionContext, collectionContext).length > 0 &&
            (!limitToExistingAssociations ||
              isBuiltInSourceVendor(r) ||
              associatedVendors.has(r.vendorId)),
        )
        ?.sort(dataSourceSort) ?? []
    );
  }, [sources, collectionContext]);

  const received = useDataMapReceived(dataMap, { collectionGroupId: relevantGroupIds });
  const collectedPIC = useDataMapCollectedPIC(dataMap, { collectionGroupId: relevantGroupIds });

  const updateReceived = ({ vendorId, receivedIds, received }: UpdateReceivedArgs) => {
    const operations: DataMapOperation[] = receivedIds.flatMap((receivedId) => {
      // only apply updates for collection groups that already collect the data
      let specificGroups = relevantGroupIds.filter((groupId) =>
        dataMap.collection.some((c) => c.groupId == groupId && c.picId == receivedId),
      );

      // only apply updates for collection groups that aren't already in the correct state
      specificGroups = specificGroups.filter((groupId) => {
        const alreadyReceived = dataMap.received.some(
          (c) => c.groupId == groupId && c.picId == receivedId && c.vendorId == vendorId,
        );

        return received != alreadyReceived;
      });

      return specificGroups.map((groupId) => {
        return {
          target: "RECEIVED",
          action: received ? "ADD" : "REMOVE",
          groupId,
          vendorId,
          picId: receivedId,
        };
      });
    });

    patchDataMap.mutate({ operations });
  };

  const complete = useMemo(() => {
    return sourcesWithCollectedPIC.every((s) => received.some((r) => r.vendorId == s.vendorId));
  }, [sourcesWithCollectedPIC, received]);

  const pendingComplete = useMemo(() => {
    return contextualSources.every((s) => received.some((r) => r.vendorId == s.vendorId));
  }, [contextualSources, received]);

  return {
    requests: {
      sourcesRequest,
      dataMapRequest,
    },
    actions: { updateReceived },
    sources: contextualSources,
    sourcesWithCollectedPIC,
    received,
    collectedPIC,
    complete,
    pendingComplete,
  };
};
